import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { filterBy } from '@progress/kendo-data-query';
import { MultiSelect } from '@progress/kendo-react-dropdowns';

import { LocationLiteModel } from 'models';

/**
 * Props for LocationLiteMultiSelect component - Kendo Form compatible
 */
export type LocationLiteMultiSelectProps = {
  /** Array of LocationLiteModel data */
  data: LocationLiteModel[];
  /** Form field name */
  name: string;
  /** HTML id attribute for the component */
  id?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether the component is loading */
  loading?: boolean;
  /** Callback fired when selection changes - Kendo Form compatible */
  onChange?: () => void;
  /** Callback fired when field loses focus */
  onBlur?: () => void;
  /** Whether filtering is enabled */
  filterable?: boolean;
  /** Callback fired when filter changes */
  onFilterChange?: (event: any) => void;
  /** Additional props passed to the underlying MultiSelect */
  [key: string]: unknown;
};

/**
 * Kendo Form compatible LocationLiteMultiSelect component
 *
 * A specialized MultiSelect component for LocationLiteModel data that works with Kendo Form system.
 * This component is designed to be used with Field component from @progress/kendo-react-form.
 *
 * @example
 * <Field
 *   component={LocationLiteMultiSelect}
 *   data={locationLiteData}
 *   editorId="locations"
 *   name="locations"
 *   onChange={handleFormChange}
 *   loading={locationsQuery.isLoading}
 * />
 */
export const LocationLiteMultiSelect: FunctionComponent<LocationLiteMultiSelectProps> = ({
  data,
  name,
  id,
  required = false,
  disabled = false,
  loading = false,
  onChange,
  onBlur,
  filterable = false,
  onFilterChange,
  ...rest
}) => {
  const [filteredData, setFilteredData] = useState<LocationLiteModel[]>([]);

  // Update filtered data when original data changes
  useEffect(() => {
    setFilteredData(data.slice());
  }, [data]);

  const handleFilterChange = useCallback(
    (event: any) => {
      if (filterable && event.filter) {
        setFilteredData(filterBy(data.slice(), event.filter));
      } else {
        setFilteredData(data.slice());
      }
      // Call custom filter change handler if provided
      onFilterChange?.(event);
    },
    [data, filterable, onFilterChange],
  );

  // Memoize props for better performance
  const multiSelectProps = useMemo(
    () => ({
      ...rest,
      data: filterable ? filteredData : data,
      name,
      id,
      textField: 'name' as const,
      dataItemKey: 'id' as const,
      disabled: disabled || loading,
      required,
      filterable,
      onFilterChange: filterable ? handleFilterChange : undefined,
      onChange,
      onBlur,
    }),
    [rest, filterable, filteredData, data, name, id, disabled, loading, required, handleFilterChange, onChange, onBlur],
  );

  return <MultiSelect {...multiSelectProps} />;
};
